<!-- @format -->
<template>
    <div class="exit-survey-response-details-widget">
        <div class="widget-header">
            <h3>Survey Response Details</h3>
            <button class="widget-close-button" @click="close">
                <widget-close-svg />
            </button>
        </div>

        <div ref="contentWrapper" class="content-wrapper">
            <div v-if="isLoading" class="loading-container">
                <loading-screen-svg />
            </div>
            <div v-show="!isLoading" class="details-container">
                <div class="details-header">
                    <div class="detail-header-left">
                        <h3>{{ responseLabel }}</h3>
                        <div>Answers: {{ details.currentCount }}/{{ details.totalResponses }}</div>
                    </div>
                    <div class="detail-header-right">
                        <h3>{{ responseCountPercentage }}%</h3>
                    </div>
                </div>

                <h3>Partners who answered:</h3>

                <div class="responses-board-container">
                    <exit-survey-response-details-board
                        :response-type="responseType"
                        :loading="isLoading"
                        :loading-failed="false"
                        :items="details.responses"
                        :total-items="details.currentCount"
                        @query="handleQuery"
                    />
                </div>
            </div>
        </div>

        <div class="widget-footer">
            <app-button class="cancel-button back-button" @click="close"> Close </app-button>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus';
import WidgetCloseSvg from '@/components/svg/quick-add/widget-close-svg.vue';
import { ExitSurveyService } from '@/scripts/services/exit-survey/exit-survey-service';
import { EXIT_SURVEY_RESPONSE_DETAILS_WIDGET_NAME } from '@/components/sliding-widgets/exit-survey-response-details-widget/exit-survey-response-details-widget';
import { EXIT_SURVEY_TYPE_LABEL_MAP } from '@/scripts/exit-survey';
import { EXIT_SURVEY_RESPONSE_TYPE } from '@/scripts/exit-survey';
import ExitSurveyResponseDetailsBoard from '@/components/sliding-widgets/exit-survey-response-details-widget/exit-survey-response-details-board.vue';

const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg.vue');
const AppButton = () => import('@/components/common/app-button.vue');

export default {
    name: EXIT_SURVEY_RESPONSE_DETAILS_WIDGET_NAME,

    components: {
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'app-button': AppButton,
        'exit-survey-response-details-board': ExitSurveyResponseDetailsBoard,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: true,
            details: {
                responses: [],
                currentCount: 0,
                totalResponses: 0,
            },
        };
    },
    computed: {
        responseType() {
            return this.widget.config.responseType;
        },

        responseLabel() {
            return EXIT_SURVEY_TYPE_LABEL_MAP[this.responseType];
        },

        responseCountPercentage() {
            return ((this.details.currentCount / this.details.totalResponses) * 100).toFixed(0);
        },

        dateRange() {
            return this.widget.config.dateRange;
        },
    },

    methods: {
        close() {
            EventBus.$emit('close-widget', this.widget);
        },

        async handleQuery(query) {

            this.isLoading = true;

            try {
                query.dateRange = this.dateRange;

                const config = this.getQueryParams(query);
                const response = await ExitSurveyService.getExitSurveyResponseDetails(config);

                this.details.currentCount = response.data.data.currentCount;
                this.details.totalResponses = response.data.data.totalResponses;
                this.details.responses = response.data.data.responses.map(this.mapResponse);
            }
            catch (error) {
                this.$helpers.showFailNotification('Failed to load exit survey details', {
                    error,
                });
            }

            finally {
                this.isLoading = false;
            }
        },

        mapResponse(item) {
            let response = {
                accountName: {
                    id: item.clientId,
                    logoUrl: item.clientLogoImageUrl,
                    name: item.accountName,
                },
                createdAt: this.$moment.utc(item.createdAt).local(),
            }

            if(this.responseType === EXIT_SURVEY_RESPONSE_TYPE.OTHER) {
                response.otherReason = item.otherReason;
            }

            return response;
        },

        getQueryParams(options) {
            return {
                filter: this.getFilterParam(options),
                order: this.getOrderParam(options),
                limit: this.getTopParam(options),
                skip: this.getPageParam(options),
                count: true,
            };
        },

        getFilterParam(options) {
            let filters = [];

            if (options.search) {
                var encodedSearch = encodeURIComponent(`%${options.search}%`);
                filters.push(`{"[AccountName]":{"$like":["${encodedSearch}"]}}`);
            }

            if (options.dateRange) {
                filters.push(`{"CreatedAt":{"$gte":"${options.dateRange.startDate}"}}`);
                filters.push(`{"CreatedAt":{"$lte":"${options.dateRange.endDate}"}}`);
            }

            filters.push(`{"${this.responseType}":{"$eq":true}}`);

            return `[${filters.join(',')}]`;
        },

        getPageParam(options) {
            return options.itemsPerPage * (options.page - 1);
        },

        getTopParam(options) {
            return options.itemsPerPage;
        },

        getOrderParam(options) {
            if (options.sort) {
                return `["${options.sort.property}", "${options.sort.direction}"]`;
            }
            
            return '';
        },
    },
};
</script>

<style>
.exit-survey-response-details-widget .loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
}

.exit-survey-response-details-widget .divider {
    margin: 24px 0;
    border-bottom: 1px solid var(--color-border-divider);
}

.exit-survey-response-details-widget .details-container h3 {
    margin-bottom: 16px;
}

.exit-survey-response-details-widget .details-header {
    display: flex;
    justify-content: space-between;
    background: var(--color-fill-field-active);
    padding: 24px;
    border-radius: 16px;
    margin-bottom: 24px;
}

.exit-survey-response-details-widget .details-header h3 {
    margin-bottom: 8px;
}

.exit-survey-details-widget .detail-header .detail-header-left h3 {
    margin-bottom: 0;
}
</style>
